import { Box } from 'components/Box'
import { Button } from 'components/Button'
import { Checkbox } from 'components/Checkbox'
import { Icon } from 'components/Icon'
import { RadioGroup } from 'components/RadioGroup/RadioGroup'
import { Select } from 'components/Select'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { TextInput } from 'components/TextInput'
import { Tooltip } from 'components/Tooltip'
import { InputLabel } from 'features/MealPlan/components/InputLabel'
import { Loader } from 'features/components/Loader'
import { useDateFormat } from 'hooks/useDateFormat'
import { useRouter } from 'hooks/useRouter'
import { useTranslation } from 'hooks/useTranslation'
import { BirthDate, PetGenders, PetKinds } from 'models/Pet'
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { getDateDiffInYearsAndMonths, getDaysInCurrentMonth } from 'utils/date'
import {
  PetAttempt,
  PetAttemptStep1,
  useSubmitPetAttempt,
  determineFeedingProductType,
} from '../../api/mutations/useSubmitPetAttempt'
import { useFetchPetAttempt } from '../../api/queries/useFetchPetAttempt'
import { MealPlanLayout } from '../../components/MealPlanLayout'
import {
  MealPlanFormContainer,
  MealPlanQuestionnaireCard,
  MealPlanRoot,
  MealPlanStepWrapper,
  MealPlanFooterOrder,
} from '../../components/MealPlanLayout/MealPlanLayout.styles'
import { getApproximatedDateOfBirth } from '../../utils/getApproximateDateOfBirth'
import { useAuth } from 'contexts/AuthContext'
import { Step1Root, BirthDateSelectWrapper } from './Step1.styles'
import { TextRadioOption } from './components/TextRadioOption'
import { fbConversion, trackPageView } from '../../../../api/analytics/analytics'
import { WizardQuoteCard } from 'features/MealPlan/components/WizardQuoteCard/WizardQuoteCard'
import amyAvatar from '../../../../../public/assets/meal-plan/quote-avatar-amy-zalneraitis.webp'

type Step1Props = {}

type formErrorObject = {
  months?: string
}

export const Step1 = (_props: Step1Props) => {
  const { t } = useTranslation()
  const { format } = useDateFormat()

  const { data: petAttemptStep1, status } = useFetchPetAttempt({ retry: false })
  const { submitPetAttempt } = useSubmitPetAttempt()
  const { promoteToGuest } = useAuth()

  const router = useRouter()

  const [loading, setLoading] = useState(true)
  const [petProfile, setPetProfile] = useState<Partial<PetAttemptStep1>>({
    isSpayed: false,
    kind: PetKinds.DOG,
  })

  const [formErrors, setFormErrors] = useState<formErrorObject>({})

  const [knownBirthDate, setKnownBirthDate] = useState(false)
  const [birthDate, setBirthDate] = useState<BirthDate>()

  const [age, setAge] = useState<{ years?: number; months?: number }>()

  const [daysInMonth, setDaysInMonth] = useState(31)

  const { MONTHS_OPTIONS, DAYS_OPTIONS, YEARS_OPTIONS, currentYear } = useMemo(() => {
    const MONTHS_OPTIONS = Array.from({ length: 12 }, (_, i) => `${i + 1}`)

    const DAYS_OPTIONS = Array.from({ length: daysInMonth }, (_, i) => `${i + 1}`)

    const currentYear = new Date().getFullYear()
    const YEARS_OPTIONS = Array.from({ length: 30 }, (_, i) => `${i + currentYear - 29}`).reverse()

    return { MONTHS_OPTIONS, DAYS_OPTIONS, YEARS_OPTIONS, currentYear }
  }, [daysInMonth])

  useEffect(() => {
    fbConversion('InitiateWizard')
    trackPageView({ contentName: '/step-1 of quiz', contentCategory: 'page_view' })
  }, [])

  useEffect(() => {
    if (status === 'loading') return
    setLoading(false)

    if (!petAttemptStep1) return

    setPetProfile({ ...petAttemptStep1, isSpayed: petAttemptStep1.isSpayed ?? false })

    if (!petAttemptStep1.birthDate) return

    setAge({
      years: getDateDiffInYearsAndMonths(petAttemptStep1.birthDate).years,
      months: getDateDiffInYearsAndMonths(petAttemptStep1.birthDate).months,
    })

    setKnownBirthDate(!!petAttemptStep1.birthDate)

    setBirthDate({
      year: parseInt(format(petAttemptStep1.birthDate, 'YEAR')),
      month: parseInt(format(petAttemptStep1.birthDate, 'MONTH_NUMBER')),
      day: parseInt(format(petAttemptStep1.birthDate, 'DAY')),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, petAttemptStep1])

  useEffect(() => {
    if (!birthDate?.month) return

    setDaysInMonth(getDaysInCurrentMonth(birthDate.year || currentYear, birthDate.month))
  }, [birthDate?.month, birthDate?.year, currentYear])

  if (loading) return <Loader />

  const ageFilled =
    (knownBirthDate && birthDate?.year && birthDate?.month && birthDate?.day) ||
    (!knownBirthDate && (age?.years || age?.months))

  const allFieldsFilled = petProfile.name && petProfile.gender && ageFilled

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPetProfile({
      ...petProfile,
      [e.target.name]: e.target.value,
    })
  }

  const handleAgeChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.value = e.target.value.replace(/\D/g, '')
    const numberValue = parseInt(e.target.value)
    setAge({ ...age, [e.target.name]: numberValue })

    if (e.target.name === 'months' && numberValue > 11) {
      setFormErrors({ months: 'Please enter a value from 0 to 11 months.' })
      return
    }

    setFormErrors({ months: '' })
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!allFieldsFilled || formErrors.months) return

    const submitData = petProfile
    petProfile.kind = PetKinds.DOG

    submitData.birthDate = knownBirthDate
      ? new Date(birthDate!.year!, birthDate!.month! - 1, birthDate!.day!).toISOString()
      : getApproximatedDateOfBirth(age?.years || 0, age?.months || 0)

    const updatedSubmitData = determineFeedingProductType(submitData)
    submitPetAttempt(
      {
        ...updatedSubmitData,
        utmCampaign: Object.keys(router.query).length > 0 ? router.query : undefined,
      } as Required<PetAttemptStep1 & Pick<PetAttempt, 'utmCampaign'>>,
      {
        onSuccess: (petAttempt) => {
          promoteToGuest(petAttempt.ownerId)
          router.replace('/meal-plan/step-2')
        },
        onError: (err) => {
          // console.log(err)
        },
      }
    )
  }

  return (
    <MealPlanRoot>
      <MealPlanLayout step={1}>
        <MealPlanStepWrapper>
          <Step1Root>
            <MealPlanQuestionnaireCard>
              <Stack
                direction="column"
                alignItems="center"
                gap={{ '@initial': '4', '@bp2': '7' }}
                as="form"
                onSubmit={handleSubmit}
              >
                <Text
                  size={{ '@initial': 'h3', '@bp2': 'h1' }}
                  font="display"
                  as="h1"
                  color="accent-black"
                  weight="semiBold"
                  align="center"
                >
                  {t('meal-plan:step-1.title')}
                </Text>
                <MealPlanFormContainer>
                  <Stack gap="6" direction="column">
                    <TextInput
                      outerLabel={(props) => (
                        <Stack alignItems="center" justifyContent="center">
                          <InputLabel {...props}>{t('meal-plan:step-1.name-question')}</InputLabel>
                          <Tooltip instant side="top" text={t('meal-plan:step-1.name-tooltip')}>
                            <Box css={{ display: 'flex', alignItems: 'center' }}>
                              <Icon name="information-circle-line" color="accent-black" />
                            </Box>
                          </Tooltip>
                        </Stack>
                      )}
                      name="name"
                      defaultValue={petProfile.name}
                      onChange={handleChange}
                      label={t('meal-plan:step-1.name')}
                      placeholder={t('meal-plan:step-1.name')}
                    />

                    <Stack direction="column">
                      <Stack direction="column" alignItems="center" gap="2">
                        <Text
                          size="headline"
                          as="label"
                          weight="bold"
                          color="accent-black"
                          align="center"
                        >
                          {t('meal-plan:step-1.age.label')}
                        </Text>
                        <Text
                          size="body"
                          align="center"
                          css={{
                            color: '$accent-dark-gray',
                            maxWidth: '300px',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            '@bp2': { maxWidth: 'none' },
                          }}
                        >
                          {t('meal-plan:step-1.age.sub-copy')}
                        </Text>
                        <Stack width="fullWidth" gap="2">
                          <TextInput
                            short
                            type="text"
                            inputMode="numeric"
                            pattern="\d*"
                            name="years"
                            defaultValue={
                              petProfile.birthDate
                                ? getDateDiffInYearsAndMonths(petProfile.birthDate).years
                                : undefined
                            }
                            onChange={handleAgeChange}
                            label={t('meal-plan:step-1.age.years')}
                            placeholder={t('meal-plan:step-1.age.years')}
                            disabled={knownBirthDate}
                          />
                          <TextInput
                            short
                            type="text"
                            inputMode="numeric"
                            pattern="\d*"
                            name="months"
                            defaultValue={
                              petProfile.birthDate
                                ? getDateDiffInYearsAndMonths(petProfile.birthDate).months
                                : undefined
                            }
                            onChange={handleAgeChange}
                            label={t('meal-plan:step-1.age.months')}
                            placeholder={t('meal-plan:step-1.age.months')}
                            disabled={knownBirthDate}
                            error={!!formErrors.months}
                            helperText={formErrors.months}
                          />
                        </Stack>
                      </Stack>

                      <Stack
                        direction="column"
                        gap={{ '@initial': '1', '@bp2': '2' }}
                        css={{ alignItems: 'center', marginTop: '10px !important' }}
                      >
                        <Checkbox
                          label={t('meal-plan:step-1.birth-date')}
                          onChange={() => setKnownBirthDate(!knownBirthDate)}
                          checked={knownBirthDate}
                          customCSS={{ fontSize: '$body', width: '100%' }}
                        />
                        {knownBirthDate && (
                          <BirthDateSelectWrapper gap={{ '@initial': '1', '@bp2': '2' }}>
                            <Select
                              isShort
                              label={t('meal-plan:step-1.mm')}
                              items={MONTHS_OPTIONS}
                              defaultValue={
                                petProfile.birthDate
                                  ? format(petProfile.birthDate, 'MONTH_NUMBER')
                                  : undefined
                              }
                              onChange={(month) =>
                                setBirthDate({ ...birthDate, month: parseInt(month) })
                              }
                            />
                            <Select
                              isShort
                              label={t('meal-plan:step-1.dd')}
                              items={DAYS_OPTIONS}
                              defaultValue={
                                petProfile.birthDate ? format(petProfile.birthDate, 'DAY') : undefined
                              }
                              onChange={(day) => setBirthDate({ ...birthDate, day: parseInt(day) })}
                            />
                            <Select
                              isShort
                              label={t('meal-plan:step-1.yyyy')}
                              items={YEARS_OPTIONS}
                              defaultValue={
                                petProfile.birthDate
                                  ? format(petProfile.birthDate, 'YEAR')
                                  : undefined
                              }
                              onChange={(year) =>
                                setBirthDate({ ...birthDate, year: parseInt(year) })
                              }
                            />
                          </BirthDateSelectWrapper>
                        )}
                      </Stack>
                    </Stack>

                    <RadioGroup.Root
                      label={t('meal-plan:step-1.gender')}
                      name="gender"
                      defaultValue={petProfile.gender}
                      direction="column"
                      pill
                      onValueChange={(value) =>
                        setPetProfile({
                          ...petProfile,
                          gender: value as PetGenders,
                        })
                      }
                    >
                      <Stack direction="column" width="fullWidth" gap="3">
                        <TextRadioOption
                          value={PetGenders.MALE}
                          label={t(`common:${PetGenders.MALE}`)}
                        />
                        <TextRadioOption
                          value={PetGenders.FEMALE}
                          label={t(`common:${PetGenders.FEMALE}`)}
                        />
                      </Stack>
                    </RadioGroup.Root>
                    {petProfile.gender && (
                      <RadioGroup.Root
                        pill
                        label={t(`meal-plan:step-1.spayed-neutered.${petProfile.gender}`)}
                        name="isSpayed"
                        direction="column"
                        defaultValue={petProfile.isSpayed ? '1' : '0'}
                        onValueChange={(value) =>
                          setPetProfile({
                            ...petProfile,
                            isSpayed: value === '1',
                          })
                        }
                      >
                        <Stack direction="column" gap="3" width="fullWidth">
                          <Text
                            size="body"
                            align="center"
                            css={{
                              color: '$accent-dark-gray',
                              maxWidth: '300px',
                              marginLeft: 'auto',
                              marginRight: 'auto',
                              '@bp2': { maxWidth: 'none' },
                            }}
                          >
                            {petProfile.gender === PetGenders.FEMALE
                              ? t('meal-plan:step-1.spayed-neutered.FEMALE-sub-copy')
                              : t('meal-plan:step-1.spayed-neutered.MALE-sub-copy')}
                          </Text>
                          <TextRadioOption value={'1'} label={t(`common:yes`)} />
                          <TextRadioOption value={'0'} label={t(`common:no`)} />
                        </Stack>
                      </RadioGroup.Root>
                    )}
                    <Button
                      variant="primary"
                      size="regular"
                      fullWidth
                      type="submit"
                      disabled={!allFieldsFilled || !!formErrors.months}
                    >
                      {t('common:continue')}
                    </Button>
                  </Stack>
                </MealPlanFormContainer>
              </Stack>
            </MealPlanQuestionnaireCard>
          </Step1Root>
          <MealPlanFooterOrder>
            <WizardQuoteCard
              quote={t('meal-plan:step-1.quote.text')}
              authorName={t('meal-plan:step-1.quote.author')}
              authorTitle={t('meal-plan:step-1.quote.authorTitle')}
              avatarSrc={amyAvatar}
              avatarAlt={t('meal-plan:step-1.quote.author')}
            />
          </MealPlanFooterOrder>
        </MealPlanStepWrapper>
      </MealPlanLayout>
    </MealPlanRoot>
  )
}

Step1.displayName = 'Step1'
